const FONT_FACE_RULE_REGEX = /@font-face\s*{[\s\S]*?}/gi;
const FONT_FAMILY_DECLARATION_REGEX = /font-family\s*:\s*([^;]+);?/i;
const FONT_FAMILY_USAGE_DECLARATION_REGEX = /font-family\s*:\s*([^;]+);?/gi;

const cleanString = (value) => (typeof value === "string" ? value.trim() : "");

const normalizeFontFamilyName = (rawFontFamily) => {
  const cleanFontFamily = cleanString(rawFontFamily).replace(/\s*!important\s*$/i, "");
  if (cleanFontFamily === "") return "";

  return cleanFontFamily
    .replace(/^['"]|['"]$/g, "")
    .trim()
    .toLowerCase();
};

const parseHtmlDocument = (htmlContent) => {
  if (typeof DOMParser === "undefined") return null;
  if (typeof htmlContent !== "string" || htmlContent.trim() === "") return null;

  try {
    return new DOMParser().parseFromString(htmlContent, "text/html");
  } catch {
    return null;
  }
};

const addFontFamiliesFromDeclaration = (rawDeclaration, usedFontFamilies) => {
  const declarationValue = cleanString(rawDeclaration);
  if (declarationValue === "") return;

  declarationValue.split(",").forEach((rawFontFamily) => {
    const fontFamily = normalizeFontFamilyName(rawFontFamily);
    if (fontFamily !== "") usedFontFamilies.add(fontFamily);
  });
};

const extractUsedFontFamilies = (htmlDocument) => {
  const usedFontFamilies = new Set();
  if (!htmlDocument) return usedFontFamilies;

  htmlDocument.querySelectorAll("[style]").forEach((element) => {
    addFontFamiliesFromDeclaration(element?.style?.fontFamily, usedFontFamilies);
  });

  htmlDocument.querySelectorAll("style").forEach((styleElement) => {
    const cssText = styleElement?.textContent || "";
    if (cssText === "") return;

    const cssWithoutFontFaceRules = cssText.replace(FONT_FACE_RULE_REGEX, " ");
    let fontFamilyMatch = FONT_FAMILY_USAGE_DECLARATION_REGEX.exec(cssWithoutFontFaceRules);
    while (fontFamilyMatch) {
      addFontFamiliesFromDeclaration(fontFamilyMatch[1], usedFontFamilies);
      fontFamilyMatch = FONT_FAMILY_USAGE_DECLARATION_REGEX.exec(cssWithoutFontFaceRules);
    }
    FONT_FAMILY_USAGE_DECLARATION_REGEX.lastIndex = 0;
  });

  return usedFontFamilies;
};

const extractFontFaceRulesByFamily = (htmlDocument) => {
  const fontFaceRulesByFamily = new Map();
  if (!htmlDocument) return fontFaceRulesByFamily;

  htmlDocument.querySelectorAll("style").forEach((styleElement) => {
    const cssText = styleElement?.textContent || "";
    if (cssText === "") return;

    const fontFaceRules = cssText.match(FONT_FACE_RULE_REGEX) || [];
    fontFaceRules.forEach((fontFaceRule) => {
      const fontFamilyMatch = fontFaceRule.match(FONT_FAMILY_DECLARATION_REGEX);
      const fontFamily = normalizeFontFamilyName(fontFamilyMatch?.[1]);
      if (fontFamily === "") return;

      const currentRules = fontFaceRulesByFamily.get(fontFamily) || [];
      if (!currentRules.includes(fontFaceRule)) {
        fontFaceRulesByFamily.set(fontFamily, [...currentRules, fontFaceRule]);
      }
    });
  });

  return fontFaceRulesByFamily;
};

const getCssDeclarationValue = (cssRule, declarationName) => {
  if (typeof cssRule !== "string" || cssRule === "") return "";

  const declarationRegex = new RegExp(`${declarationName}\\s*:\\s*([^;]+);?`, "i");
  const declarationMatch = cssRule.match(declarationRegex);
  return cleanString(declarationMatch?.[1])
    .replace(/^['"]|['"]$/g, "")
    .toLowerCase();
};

const getFontFaceVariantKey = (fontFaceRule) => {
  const fontFamilyMatch = fontFaceRule.match(FONT_FAMILY_DECLARATION_REGEX);
  const fontFamily = normalizeFontFamilyName(fontFamilyMatch?.[1]);
  if (fontFamily === "") return "";

  const fontStyle = getCssDeclarationValue(fontFaceRule, "font-style") || "normal";
  const fontWeight = getCssDeclarationValue(fontFaceRule, "font-weight") || "normal";
  const fontStretch = getCssDeclarationValue(fontFaceRule, "font-stretch") || "normal";
  const unicodeRange = getCssDeclarationValue(fontFaceRule, "unicode-range") || "*";
  const source = getCssDeclarationValue(fontFaceRule, "src");

  return `${fontFamily}|${fontStyle}|${fontWeight}|${fontStretch}|${unicodeRange}|${source}`;
};

const extractAdditionalDocumentIds = (convertedFiles = []) =>
  (Array.isArray(convertedFiles) ? convertedFiles : [])
    .map((file) => {
      if (typeof file === "string") return cleanString(file);
      return cleanString(file?.id || file?.documentId || file?.shortGuid);
    })
    .filter(Boolean);

const recoverMissingFontFacesInHtml = async ({
  htmlContent,
  convertedFiles = [],
  getAdditionalDocument,
}) => {
  const htmlDocument = parseHtmlDocument(htmlContent);
  if (!htmlDocument) return htmlContent;

  const usedFontFamilies = extractUsedFontFamilies(htmlDocument);
  if (usedFontFamilies.size === 0) return htmlContent;
  if (typeof getAdditionalDocument !== "function") return htmlContent;

  const additionalDocumentIds = extractAdditionalDocumentIds(convertedFiles);
  if (additionalDocumentIds.length === 0) return htmlContent;

  const additionalDocumentHtmlList = await Promise.all(
    additionalDocumentIds.map((documentId) =>
      getAdditionalDocument(documentId)
        .then(({ status, data }) =>
          status === 200 && typeof data?.htmlFile === "string" ? data.htmlFile : null
        )
        .catch(() => null)
    )
  );

  const sourceFontFaceRulesByFamilyList = additionalDocumentHtmlList
    .map(parseHtmlDocument)
    .filter(Boolean)
    .map(extractFontFaceRulesByFamily);

  if (sourceFontFaceRulesByFamilyList.length === 0) return htmlContent;

  const existingFontFaceRulesByFamily = extractFontFaceRulesByFamily(htmlDocument);
  const recoveredFontFaceRules = [];

  usedFontFamilies.forEach((fontFamily) => {
    const existingRules = existingFontFaceRulesByFamily.get(fontFamily) || [];
    const existingRuleTexts = new Set(existingRules.map(cleanString).filter(Boolean));
    const existingVariantKeys = new Set(existingRules.map(getFontFaceVariantKey).filter(Boolean));

    sourceFontFaceRulesByFamilyList.forEach((sourceFontFaceRulesByFamily) => {
      const sourceRules = sourceFontFaceRulesByFamily.get(fontFamily) || [];

      sourceRules.forEach((sourceRule) => {
        const sourceRuleText = cleanString(sourceRule);
        if (sourceRuleText === "" || existingRuleTexts.has(sourceRuleText)) return;

        const sourceRuleVariantKey = getFontFaceVariantKey(sourceRule);
        if (sourceRuleVariantKey !== "" && existingVariantKeys.has(sourceRuleVariantKey)) return;

        recoveredFontFaceRules.push(sourceRule);
        existingRuleTexts.add(sourceRuleText);
        if (sourceRuleVariantKey !== "") {
          existingVariantKeys.add(sourceRuleVariantKey);
        }
      });
    });
  });

  if (recoveredFontFaceRules.length === 0) return htmlContent;

  const recoveredStyleElement = htmlDocument.createElement("style");
  recoveredStyleElement.setAttribute("type", "text/css");
  recoveredStyleElement.textContent = `\n${[...new Set(recoveredFontFaceRules)].join("\n")}\n`;

  if (htmlDocument.head) {
    htmlDocument.head.appendChild(recoveredStyleElement);
  } else if (htmlDocument.documentElement) {
    htmlDocument.documentElement.prepend(recoveredStyleElement);
  }

  return htmlDocument.documentElement?.outerHTML || htmlContent;
};

export default recoverMissingFontFacesInHtml;
